import {
	ApartmentOutlined,
	AuditOutlined,
	BankOutlined,
	BarChartOutlined,
	BellOutlined,
	CalendarOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	ContactsOutlined,
	DashboardOutlined,
	DeleteOutlined,
	EnvironmentOutlined,
	FieldTimeOutlined,
	FileTextOutlined,
	FlagOutlined,
	FormOutlined,
	GlobalOutlined,
	IdcardOutlined,
	LineChartOutlined,
	LogoutOutlined,
	MenuFoldOutlined,
	MenuUnfoldOutlined,
	MobileOutlined,
	MoonOutlined,
	NotificationOutlined,
	ProjectOutlined,
	SafetyOutlined,
	ScheduleOutlined,
	SettingOutlined,
	SunOutlined,
	TeamOutlined,
	ThunderboltOutlined,
	ToolOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Outlet, useLocation, useMatches, useNavigate } from "@tanstack/react-router";
import {
	Avatar,
	Button,
	Dropdown,
	Layout,
	Menu,
	type MenuProps,
	Segmented,
	Switch,
	Typography,
} from "antd";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AppLocale } from "@/app/i18n/constants";
import { useChangeLocale } from "@/app/i18n/useChangeLocale";
import { useColorScheme } from "@/app/providers/useColorScheme";
import { ProtectedRoute } from "@/app/router/ProtectedRoute";
import { useLogout } from "@/modules/auth/hooks/useAuth";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { usePermission } from "@/shared/hooks/usePermission";
import { useRecentlyViewed } from "@/shared/hooks/useRecentlyViewed";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

/**
 * Kontent maydonining id'si — ilovada scroll bo'ladigan YAGONA konteyner.
 * ⚠️ `router/index.tsx` dagi `scrollToTopSelectors` shu id'ni oladi: sahifa
 * almashganda router faqat `window` ni tepaga qaytaradi, `window` esa endi
 * scroll bo'lmaydi. Id mos kelmasa `querySelector` null qaytaradi, router uni
 * jimgina yutadi va yangi sahifa eskisining scroll o'rnida ochilardi.
 */
export const MAIN_SCROLL_ID = "main-scroll";

type Can = (resource: string, action: string) => boolean;
/** `useTranslation("shared")`ning `t` funksiyasi — `buildMenuItems` sinovlarda
 * `t` ni bermasdan chaqirilishi mumkin, shuning uchun standart qiymati bor
 * (pastga qarang) va bu tur faqat kalitni oladigan eng tor shaklda. */
type T = (key: string) => string;
type LeafItem = { key: string; icon: ReactNode; label: string };
type MenuItem = NonNullable<MenuProps["items"]>[number];

/** Ruxsat bo'lsa bandlarni, aks holda bo'sh massiv qaytaradi. */
function when(allowed: boolean, ...items: LeafItem[]): LeafItem[] {
	return allowed ? items : [];
}

/**
 * Bo'lim (submenu) yasaydi. ⚠️ Bandlari BO'SH bo'lsa umuman qaytarilmaydi —
 * aks holda ruxsati tor foydalanuvchi ochilganda ichi bo'sh bo'lgan sarlavhani
 * ko'rardi (`manager` da «Tizim» aynan shunday: `settings`, `jobs` va
 * `retention` ruxsatlarining birortasi ham yo'q — `packages/db/src/seed.ts`
 * bilan tekshirilgan. «Kirish va xavfsizlik» esa BO'SH EMAS: `manager` da
 * `devices:read` bor).
 *
 * ⚠️ Bo'lim kaliti `sec:` prefiksi bilan — route yo'llari bilan to'qnashmasligi
 * uchun (`selectedKeys` faqat barg bandlarga tegishi shart).
 */
function section(key: string, icon: ReactNode, label: string, children: LeafItem[]): MenuItem[] {
	return children.length > 0 ? [{ key: `sec:${key}`, icon, label, children }] : [];
}

/**
 * Yon menyu daraxti. Ruxsati yo'q band menyuda umuman ko'rinmaydi — bu
 * qulaylik uchun, haqiqiy himoya backend'da va `ProtectedRoute` da.
 *
 * ⚠️ `t` IXTIYORIY (standart — kalitni o'zini qaytaruvchi identity funksiya).
 * `MainLayout.test.tsx` bu funksiyani faqat `can` bilan chaqiradi va faqat
 * `key`larni tekshiradi (`label`ga tegmaydi) — standart qiymat shu testlarni
 * o'zgartirmasdan saqlab qoladi. Haqiqiy renderda `MainLayout`
 * `useTranslation("shared")`dan olingan haqiqiy `t`ni beradi.
 */
export function buildMenuItems(can: Can, t: T = (key) => key): MenuItem[] {
	return [
		{ key: "/dashboard", icon: <DashboardOutlined />, label: t("menu.dashboard") },

		/*
		 * ⚠️ **Jonli xarita YUQORI darajada.** U kunlik jurnal emas, real
		 * vaqt nazorat ekrani — rahbar uni kun davomida ochiq tutadi.
		 * «Davomat» guruhi ichida u har safar ikki bosishni talab qilardi.
		 */
		...when(can("attendance", "read"), {
			key: "/attendance/live-map",
			icon: <GlobalOutlined />,
			label: t("menu.liveMap"),
		}),

		/*
		 * ⚠️ **«Bugungi holat» (`/attendance/dashboard`) OLIB TASHLANDI** —
		 * u boshqaruv panelidagi kartalarning qism to'plami edi va ikki
		 * menyu bandi bitta savolga javob berardi. Marshrut
		 * `routeTree.tsx` da `/dashboard` ga yo'naltiriladi.
		 *
		 * ⚠️ Uchta jadval elementi BITTA `can("schedules", "read")`
		 * tekshiruvida — ular bitta RBAC resursiga tegishli.
		 * `can("holidays", "read")` yozilsa menyu elementi hech qachon
		 * ko'rinmasdi.
		 */
		...section("davomat", <FieldTimeOutlined />, t("menu.sections.attendance"), [
			...when(can("attendance", "read"), {
				key: "/attendance",
				icon: <CheckCircleOutlined />,
				label: t("menu.items.attendanceDays"),
			}),
			...when(
				can("schedules", "read"),
				{ key: "/schedules", icon: <ScheduleOutlined />, label: t("menu.items.schedules") },
				{
					key: "/shift-instances",
					icon: <ClockCircleOutlined />,
					label: t("menu.items.shiftInstances"),
				},
				{ key: "/holidays", icon: <CalendarOutlined />, label: t("menu.items.holidays") }
			),
			...when(can("absences", "read"), {
				key: "/absences",
				icon: <FileTextOutlined />,
				label: t("menu.items.absences"),
			}),
		]),

		...section("tashkilot", <BankOutlined />, t("menu.sections.organization"), [
			...when(can("employees", "read"), {
				key: "/employees",
				icon: <TeamOutlined />,
				label: t("menu.items.employees"),
			}),
			...when(can("departments", "read"), {
				key: "/departments",
				icon: <ApartmentOutlined />,
				label: t("menu.items.departments"),
			}),
			...when(can("positions", "read"), {
				key: "/positions",
				icon: <IdcardOutlined />,
				label: t("menu.items.positions"),
			}),
			...when(can("zones", "read"), {
				key: "/zones",
				icon: <EnvironmentOutlined />,
				label: t("menu.items.zones"),
			}),
		]),

		...section("faoliyat", <ProjectOutlined />, t("menu.sections.field"), [
			...when(can("tasks", "read"), {
				key: "/tasks",
				icon: <FlagOutlined />,
				label: t("menu.items.tasks"),
			}),
			...when(can("leads", "read"), {
				key: "/leads",
				icon: <ContactsOutlined />,
				label: t("menu.items.leads"),
			}),
			...when(can("lead-forms", "read"), {
				key: "/lead-forms",
				icon: <FormOutlined />,
				label: t("menu.items.leadForms"),
			}),
		]),

		...section("nazorat", <LineChartOutlined />, t("menu.sections.analytics"), [
			...when(can("reports", "read"), {
				key: "/reports",
				icon: <BarChartOutlined />,
				label: t("menu.items.reports"),
			}),
			...when(can("audit", "read"), {
				key: "/audit",
				icon: <AuditOutlined />,
				label: t("menu.items.audit"),
			}),
		]),

		/*
		 * ⚠️ **«Tizim» IKKIGA bo'lindi.** Oldin bitta guruhda 7 band bor
		 * edi va u yerda ikki xil ish aralashgan: KIM kira oladi
		 * (foydalanuvchilar, rollar, qurilmalar) va tizim QANDAY ishlaydi
		 * (sozlamalar, kanallar, fon vazifalari, saqlash muddati).
		 *
		 * ⚠️ `jobs` va `retention` FAQAT `admin` da (kontrakt §5.7) — ular
		 * «Tizim» da, ya'ni `manager` da bu guruh butunlay yopiladi.
		 */
		...section("kirish", <SafetyOutlined />, t("menu.sections.access"), [
			...when(can("users", "read"), {
				key: "/users",
				icon: <UserOutlined />,
				label: t("menu.items.users"),
			}),
			...when(can("roles", "read"), {
				key: "/roles",
				icon: <SafetyOutlined />,
				label: t("menu.items.roles"),
			}),
			...when(can("devices", "read"), {
				key: "/devices",
				icon: <MobileOutlined />,
				label: t("menu.items.devices"),
			}),
		]),

		...section("tizim", <ToolOutlined />, t("menu.sections.system"), [
			...when(
				can("settings", "read"),
				{ key: "/settings", icon: <SettingOutlined />, label: t("menu.items.settings") },
				{
					key: "/notification-channels",
					icon: <NotificationOutlined />,
					label: t("menu.items.notificationChannels"),
				}
			),
			...when(can("jobs", "read"), {
				key: "/jobs",
				icon: <ThunderboltOutlined />,
				label: t("menu.items.jobs"),
			}),
			...when(can("retention", "read"), {
				key: "/retention",
				icon: <DeleteOutlined />,
				label: t("menu.items.retention"),
			}),
		]),
	];
}

/**
 * Joriy yo'l uchun eng UZUN mos keluvchi bargni va uning bo'limini topadi.
 * ⚠️ Eng uzun prefiks tanlanadi, birinchi mos kelgani emas: `/attendance`
 * ham, `/attendance/live-map` ham prefiks bo'ladi va qisqasi g'olib
 * chiqsa, jonli xarita sahifasida «Davomat kunlari» bandi yonardi.
 */
export function findActive(items: MenuItem[], pathname: string) {
	let selectedKey: string | undefined;
	let sectionKey: string | undefined;
	let bestLength = 0;

	const visit = (item: MenuItem, parentKey?: string) => {
		if (!(item && "key" in item) || typeof item.key !== "string") {
			return;
		}
		if ("children" in item && item.children) {
			for (const child of item.children as MenuItem[]) {
				visit(child, item.key as string);
			}
			return;
		}
		const key = item.key;
		if ((pathname === key || pathname.startsWith(`${key}/`)) && key.length > bestLength) {
			bestLength = key.length;
			selectedKey = key;
			sectionKey = parentKey;
		}
	};

	for (const item of items) {
		visit(item);
	}
	return { selectedKey, sectionKey };
}

/**
 * Joriy mos kelgan route'ning `staticData.requirePermission` sini o'qib,
 * `ProtectedRoute` ga uzatadi. Permission talabi route'ning o'zida
 * e'lon qilinadi (`routeTree.tsx`) — bu yerda qattiq kodlangan xarita yo'q.
 */
function LayoutContent() {
	const matches = useMatches();
	const requirePermission = matches
		.map((m) => m.staticData?.requirePermission)
		.filter(Boolean)
		.at(-1);

	return (
		<ProtectedRoute requirePermission={requirePermission}>
			<Outlet />
		</ProtectedRoute>
	);
}

export function MainLayout() {
	const [collapsed, setCollapsed] = useState(false);
	const { can } = usePermission();
	const { scheme, toggle: toggleColorScheme } = useColorScheme();
	const navigate = useNavigate();
	const location = useLocation();
	const user = useAuthStore((state) => state.user);
	const { mutate: logout, isPending: isLoggingOut } = useLogout();
	const { items: recent } = useRecentlyViewed();
	const { t, i18n } = useTranslation("shared");
	const changeLocale = useChangeLocale();

	const menuItems = buildMenuItems(can, t);
	const { selectedKey, sectionKey } = findActive(menuItems, location.pathname);

	// ⚠️ `defaultOpenKeys` YETMAYDI — u faqat mount paytida qo'llanadi.
	// Boshqaruv panelidagi kartadan `/employees` ga o'tilganda «Tashkilot»
	// yopiq qolib, faol band ko'rinmay qolardi. Shuning uchun boshqariladigan
	// `openKeys` va yo'l o'zgarganda faol bo'limni QO'SHIB qo'yish
	// (almashtirish emas — foydalanuvchi qo'lda ochgan bo'limlar yopilmasin).
	const [openKeys, setOpenKeys] = useState<string[]>(sectionKey ? [sectionKey] : []);

	useEffect(() => {
		if (!sectionKey) {
			return;
		}
		setOpenKeys((prev) => (prev.includes(sectionKey) ? prev : [...prev, sectionKey]));
	}, [sectionKey]);

	const userMenuItems = [
		{ key: "profile", icon: <UserOutlined />, label: t("userMenu.profile") },
		{ type: "divider" as const },
		{ key: "logout", icon: <LogoutOutlined />, label: t("userMenu.logout"), danger: true },
	];

	const handleUserMenuClick = ({ key }: { key: string }) => {
		if (key === "logout") {
			logout();
		}
		// "profile" hali yo'q — bu bosqichda profil sahifasi/route'i mavjud emas
		// (routeTree faqat /login, /dashboard, /users, /roles ni belgilaydi).
	};

	/**
	 * ⚠️ Yig'ilgan (`collapsed`) menyuda antd bo'limlarni popup qilib ko'rsatadi
	 * va sichqoncha ketganda `onOpenChange([])` yuboradi — ya'ni `openKeys`
	 * bo'shab qoladi. Yoyilganda faol bo'lim yana ochiq bo'lishi uchun uni
	 * shu yerda qaytaramiz.
	 */
	const toggleCollapsed = () => {
		const next = !collapsed;
		setCollapsed(next);
		if (!next && sectionKey) {
			setOpenKeys((keys) => (keys.includes(sectionKey) ? keys : [...keys, sectionKey]));
		}
	};

	const handleMenuClick = ({ key }: { key: string }) => {
		navigate({ to: key });
	};

	return (
		// ⚠️ Layout ekran balandligiga QULFLANGAN (`h-dvh overflow-hidden`):
		// sarlavha va yon menyu joyida turadi, faqat `Content` scroll bo'ladi.
		// `dvh`, `vh` EMAS — mobil brauzerda `100vh` manzil satri ostiga kirib,
		// body baribir scroll bo'lardi.
		<Layout className="h-dvh overflow-hidden">
			<Sider
				trigger={null}
				collapsible
				collapsed={collapsed}
				width={248}
				className="border-r border-[#e5e6e3] dark:border-[#2a2e33]"
				// ⚠️ `flex flex-col` `className` da EMAS — antd bolalarni ichki
				// `.ant-layout-sider-children` div'iga o'raydi, `className` esa
				// tashqi `<aside>` ga tushadi. Flex konteksti bir daraja
				// yuqorida qolgani uchun pastdagi `mt-auto` normal oqimda 0 ga
				// hisoblanardi. `classNames.body` flex'ni to'g'ridan-to'g'ri
				// ichki `.ant-layout-sider-children` ga tushiradi.
				classNames={{ body: "flex flex-col" }}
			>
				<div className="h-14 shrink-0 flex items-center gap-2.5 px-4 border-b border-[#eeefec] dark:border-[#22262b]">
					<div className="w-6.5 h-6.5 rounded-[7px] bg-[#1c5cab] text-white flex items-center justify-center text-xs font-bold">
						SK
					</div>
					{collapsed ? null : (
						<div className="leading-tight">
							<div className="text-[13.5px] font-semibold">{t("brand.name")}</div>
							<Text type="secondary" className="!text-[10.5px] uppercase tracking-wider">
								{t("brand.org")}
							</Text>
						</div>
					)}
				</div>
				<Menu
					mode="inline"
					selectedKeys={selectedKey ? [selectedKey] : []}
					openKeys={openKeys}
					onOpenChange={(keys) => setOpenKeys(keys as string[])}
					items={menuItems}
					onClick={handleMenuClick}
					// ⚠️ Yon menyu ekran balandligida — barcha bo'limlar ochiq bo'lsa
					// pastki bandlar sig'maydi, shuning uchun menyu O'Z ichida scroll
					// bo'ladi (brend va «Yaqinda ko'rilgan» joyida qoladi). Yig'ilgan
					// rejimdagi popup'lar body'ga portal qilinadi — kesilmaydi.
					className="!border-e-0 min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
				/>
				{/*
				 * ⚠️ Ro'yxat BO'SH bo'lsa sarlavha ham chizilmaydi — ichi
				 * bo'sh «Yaqinda ko'rilgan» bandi yangi foydalanuvchida
				 * nuqson bo'lib ko'rinardi. Yig'ilgan menyuda ham
				 * yashiriladi: 248 px o'rniga 80 px da yorliq sig'maydi.
				 */}
				{collapsed || recent.length === 0 ? null : (
					<div className="mt-auto shrink-0 border-t border-[#eeefec] p-2.5 dark:border-[#22262b]">
						<div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#8a929e] dark:text-[#6b7280]">
							{t("recentlyViewed.title")}
						</div>
						{recent.map((entry) => (
							<button
								key={entry.path}
								type="button"
								onClick={() => navigate({ to: entry.path })}
								className="flex h-7.5 w-full items-center rounded-md px-2 text-left text-xs text-[#565e6b] hover:bg-[#f2f2ef] dark:text-[#a3aab5] dark:hover:bg-[#1d2126]"
							>
								<span className="truncate">{entry.label}</span>
							</button>
						))}
					</div>
				)}
			</Sider>
			<Layout>
				<Header className="px-4 flex items-center gap-3 border-b border-[#e5e6e3] dark:border-[#2a2e33]">
					<Button
						type="text"
						icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
						onClick={toggleCollapsed}
					/>
					<div className="ml-auto flex items-center gap-3">
						{/*
						 * ⚠️ Til almashtirish (P7 spec §6) — dark-mode toggle bilan
						 * bitta "sozlamalar burchagi"da. Nomlar TARJIMA QILINMAYDI
						 * (`O'zbekcha`/`Русский` — har til o'z ENDONIMIDA, `shared`
						 * namespace ikkalasida ham bir xil qiymat). `changeLocale`
						 * async: `i18next.changeLanguage` + `localStorage` +
						 * `apiClient` sarlavhasi + (autentifikatsiyalangan bo'lsa)
						 * `PATCH /users/me/locale` — hammasi `useChangeLocale()` ichida.
						 */}
						<Segmented
							value={i18n.language === "uz" ? "uz" : "ru"}
							onChange={(value) => changeLocale(value as AppLocale)}
							options={[
								{ label: t("header.locale.uz"), value: "uz" },
								{ label: t("header.locale.ru"), value: "ru" },
							]}
							size="small"
							aria-label={t("header.locale.aria")}
						/>
						{/*
						 * ⚠️ Dark mode toggle — P2 spec §4. 2 holatli `Switch`,
						 * quyosh/oy ikonkasi bilan. Holat `useColorScheme()` da
						 * (`AntdProvider` bilan bitta manba) — bosilganda tema
						 * DARHOL almashadi, sahifa qayta yuklanmaydi.
						 */}
						<Switch
							checked={scheme === "dark"}
							onChange={toggleColorScheme}
							checkedChildren={<MoonOutlined />}
							unCheckedChildren={<SunOutlined />}
							aria-label={t("header.darkModeAria")}
						/>
						{/*
						 * ⚠️ «Bildirishnomalar» menyudan SHU YERGA ko'chdi — u
						 * yagona ruxsatsiz band edi (KONTRAKT NUQSONI N7) va
						 * guruhlar orasida osilib turardi. Joyi barcha rollarda
						 * bir xil.
						 */}
						<Button
							type="text"
							icon={<BellOutlined />}
							onClick={() => navigate({ to: "/notifications" })}
							title={t("header.notifications")}
						/>
						<Dropdown
							menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
							placement="bottomRight"
							trigger={["click"]}
							disabled={isLoggingOut}
						>
							<div className="flex items-center gap-2 cursor-pointer hover:bg-[#f2f2ef] px-2 py-1 rounded-md dark:hover:bg-[#1d2126]">
								<Avatar size={28} icon={<UserOutlined />} />
								<Text className="hidden sm:inline">{user?.phone}</Text>
							</div>
						</Dropdown>
					</div>
				</Header>
				{/*
				 * ⚠️ `Content` — scroll konteyneri, oq karta esa uning ICHIDA.
				 * `min-h-0` majburiy: flex bolasi standart `min-height: auto` bilan
				 * qisqarmaydi va overflow hosil bo'lmay, butun layout cho'zilardi.
				 */}
				<Content id={MAIN_SCROLL_ID} className="min-h-0 flex-1 overflow-y-auto">
					<div className="m-4 p-6 bg-white rounded-lg min-h-70 dark:bg-[#181b1f]">
						<LayoutContent />
					</div>
				</Content>
			</Layout>
		</Layout>
	);
}
